import { useState, useEffect, useRef } from 'react';
import './App.css';

const API_URL = (process.env.REACT_APP_API_URL ?? '').trim().replace(/\/+$/, '');

function CodePreview({ templateId, isOwner, onClose }) {
  const [code, setCode] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const loadCode = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateId)}/code`);
        if (!response.ok) {
          throw new Error(`Failed to load code: ${response.status}`);
        }
        const data = await response.json();
        if (data.success) {
          setCode(data.html_content);
          setOriginalCode(data.html_content);
        } else {
          throw new Error(data.error || 'Failed to load code');
        }
      } catch (err) {
        console.error('Error loading code:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (templateId) {
      loadCode();
    }
  }, [templateId]);

  const handleSave = async () => {
    if (!isOwner) {
      alert('You do not have permission to edit this template.');
      return;
    }

    setIsSaving(true);
    setSaveStatus('');
    setError(null);

    try {
      const sessionId = localStorage.getItem('app_session_id');
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateId)}/code`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          html_content: code,
          owner_id: sessionId
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setOriginalCode(code);
        setSaveStatus('Saved successfully!');
        setTimeout(() => {
          setSaveStatus('');
          // Reload the iframe to show changes
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(data.error || 'Failed to save code');
      }
    } catch (err) {
      console.error('Error saving code:', err);
      setError(err.message);
      setSaveStatus('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormat = () => {
    // Simple HTML formatting (basic indentation)
    let formatted = code;
    let indent = 0;
    const indentSize = 2;
    
    formatted = formatted
      .replace(/></g, '>\n<')
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        
        if (trimmed.startsWith('</')) {
          indent = Math.max(0, indent - indentSize);
        }
        
        const indented = ' '.repeat(indent) + trimmed;
        
        if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
          indent += indentSize;
        }
        
        return indented;
      })
      .join('\n');
    
    setCode(formatted);
  };

  const hasChanges = code !== originalCode;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content code-preview-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', width: '1200px', height: '90vh' }}>
        <div className="modal-header">
          <h2>Code Preview - {templateId}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 120px)', padding: '0' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <div className="board-loading-spinner">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
            </div>
          ) : error ? (
            <div style={{ padding: '20px', color: 'red' }}>
              <p>Error: {error}</p>
            </div>
          ) : (
            <>
              <div style={{ 
                padding: '12px 20px', 
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--bg-secondary)'
              }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {!isOwner && (
                    <span style={{ 
                      padding: '4px 12px', 
                      backgroundColor: '#fef3c7', 
                      color: '#92400e',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      Read Only
                    </span>
                  )}
                  {hasChanges && (
                    <span style={{ 
                      padding: '4px 12px', 
                      backgroundColor: '#dbeafe', 
                      color: '#1e40af',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      Unsaved Changes
                    </span>
                  )}
                  {saveStatus && (
                    <span style={{ 
                      padding: '4px 12px', 
                      backgroundColor: '#d1fae5', 
                      color: '#065f46',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {saveStatus}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleFormat}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'var(--panel)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Format
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!isOwner || !hasChanges || isSaving}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: isOwner && hasChanges && !isSaving ? 'var(--accent)' : 'var(--border)',
                      color: isOwner && hasChanges && !isSaving ? 'white' : 'var(--muted)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (isOwner && hasChanges && !isSaving) ? 'pointer' : 'not-allowed',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <textarea
                ref={textareaRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={!isOwner}
                style={{
                  flex: 1,
                  width: '100%',
                  padding: '16px',
                  fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: '#1e293b',
                  color: '#e2e8f0',
                  resize: 'none',
                  overflow: 'auto',
                  tabSize: 2
                }}
                spellCheck={false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CodePreview;

